import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { handoverDialogStore } from '@/lib/clientHandoverDialog';
import { validClientHandover } from '@/lib/clientWorkstreams';
import { typedSelect } from '@/lib/supabaseTypedQuery';
import type { ClientHandover } from '@/types/client-workstreams';
import { useAuth } from '@/contexts/AuthContext';

function HandoverForm({ workId, workspaceId, draft, existing }: { workId: string; workspaceId: string; draft?: ClientHandover; existing?: boolean }) {
  const [services, setServices] = useState(draft?.services ?? true);
  const [software, setSoftware] = useState(draft?.software ?? false);
  const [startsOn, setStartsOn] = useState(draft?.starts_on ?? '');
  const [endsOn, setEndsOn] = useState(draft?.ends_on ?? '');
  const sold = useQuery({
    queryKey: ['handover-sold-context', workspaceId, workId],
    queryFn: async () => {
      const { data, error } = await typedSelect<{ title: string; service_offered: string | null }>('deals', 'title,service_offered').eq('workspace_id', workspaceId).eq('id', workId).single();
      if (error) throw error;
      if (!data) throw new Error('Sale unavailable');
      return data;
    },
  });
  const handover = { services, software, starts_on: software ? startsOn || null : null, ends_on: software ? endsOn || null : null };
  return <>
    <DialogHeader><DialogTitle>Set up the client work</DialogTitle><DialogDescription>
      Choose what this sale includes. Each part gets its own client card. The sale is counted once.
    </DialogDescription></DialogHeader>
    {sold.isError ? <p role="alert">The sale could not be loaded. Close and try again.</p> : <p>{sold.data?.title}{sold.data?.service_offered && ` · ${sold.data.service_offered}`}</p>}
    <Label className="flex items-center gap-2"><Checkbox checked={services} onCheckedChange={value => setServices(value === true)} />Services</Label>
    <Label className="flex items-center gap-2"><Checkbox checked={software} onCheckedChange={value => setSoftware(value === true)} />Software licence (including a bundled first year)</Label>
    {software && <div className="grid grid-cols-2 gap-3">
      <Label>Licence starts<Input aria-label="Licence starts" type="date" value={startsOn} onChange={event => setStartsOn(event.target.value)} /></Label>
      <Label>Renewal date<Input aria-label="Renewal date" type="date" value={endsOn} min={startsOn} onChange={event => setEndsOn(event.target.value)} /></Label>
    </div>}
    <DialogFooter><Button variant="outline" onClick={() => handoverDialogStore.finish(null)}>Cancel</Button>
      <Button disabled={!validClientHandover(handover) || !sold.data || sold.isError} onClick={() => handoverDialogStore.finish(handover)}>{existing ? 'Create client cards' : 'Confirm win and create client cards'}</Button>
    </DialogFooter>
  </>;
}

export function ClientHandoverDialog() {
  const { user } = useAuth();
  const request = useSyncExternalStore(handoverDialogStore.subscribe, handoverDialogStore.getSnapshot);
  const actor = useRef(user?.id);
  useEffect(() => {
    if (actor.current !== user?.id || (request && request.actorId !== user?.id)) handoverDialogStore.reset();
    actor.current = user?.id;
  }, [request, user?.id]);
  useEffect(() => () => handoverDialogStore.reset(), []);
  const visible = request?.actorId === user?.id ? request : null;
  return <Dialog open={!!visible} onOpenChange={open => { if (!open) handoverDialogStore.finish(null); }}>
    <DialogContent>{visible && <HandoverForm key={visible.workId} workId={visible.workId} workspaceId={visible.workspaceId} draft={visible.draft} existing={visible.existing} />}</DialogContent>
  </Dialog>;
}
